import {Bars3Icon, CheckIcon, XMarkIcon} from '@heroicons/react/24/outline';
import React, {ReactNode, useCallback, useState} from 'react';

import {AudioOffIcon, AudioOnIcon, FullScreenIcon, NormalScreenIcon, RestartIcon} from '../../../../resources/Icons';
import Row from '../../../common/Row';
import {useBreakpoint} from '../../../common/util/useBreakpoint';
import ModalButton from '../ModalButton';
import Dropdown from './Dropdown';
import NormalButton from './NormalButton';
import ToggleButton from './ToggleButton';

type GameMenuButtonsType = {
  audioToggled: boolean;
  fullscreenToggled: boolean;
  hideFullscreenButton: boolean;
  hideRestartButton: boolean;
  onAudioClick: () => void;
  onFullscreenClick: () => void;
  onRestartClick: () => void;
};

const GameMenuButtons = ({
  audioToggled,
  fullscreenToggled,
  hideFullscreenButton,
  hideRestartButton,
  onAudioClick,
  onFullscreenClick,
  onRestartClick,
}: GameMenuButtonsType) => {
  const [forceReset, setForceReset] = useState(0);
  const {isMobile} = useBreakpoint('mobile');

  const onCloseClick = useCallback(() => {
    setForceReset(prevState => prevState + 1);
  }, []);

  const Container = useCallback(
    ({children}: {children: ReactNode}) => {
      if (isMobile && !hideFullscreenButton) {
        return (
          <Dropdown
            className="!p-xs"
            buttonIcon={
              <>
                <XMarkIcon className="h-sm w-sm hidden group-focus-within:block" aria-label={'Close menu'} />
                <Bars3Icon className="h-sm w-sm block group-focus-within:hidden" aria-label={'Open menu'} />
              </>
            }
            buttonLine1={''}
            verticalLineCentered
          >
            {children}
          </Dropdown>
        );
      }
      return <Row className="space-x-sm mobile:space-x-xs">{children}</Row>;
    },
    [hideFullscreenButton, isMobile]
  );

  return (
    <Container>
      <ToggleButton
        settingOne={<AudioOnIcon className="h-sm w-sm" />}
        settingTwo={<AudioOffIcon className="h-sm w-sm" />}
        toggled={audioToggled}
        onClick={onAudioClick}
        aria-label={audioToggled ? 'Disable audio' : 'Enable audio'}
      />
      {!hideFullscreenButton && (
        <ToggleButton
          settingOne={<NormalScreenIcon className="h-sm w-sm" />}
          settingTwo={<FullScreenIcon className="h-sm w-sm" />}
          toggled={fullscreenToggled}
          onClick={onFullscreenClick}
          aria-label={fullscreenToggled ? 'Exit fullscreen' : 'Enter fullscreen'}
        />
      )}
      {!hideRestartButton && (
        <ModalButton
          hideCloseButton
          forceReset={forceReset}
          button={
            <NormalButton aria-label={'Restart game'}>
              <RestartIcon className="h-sm w-sm" />
            </NormalButton>
          }
          bottom={
            <Row className="px-xl justify-center space-x-md">
              <NormalButton light onClick={onCloseClick}>
                <Row className="items-center w-[18rem] mobile:w-auto mobile:gap-xs justify-between font-medium">
                  <XMarkIcon className="w-md" />
                  No
                  <div className="w-md" />
                </Row>
              </NormalButton>
              <NormalButton onClick={onRestartClick}>
                <Row className="items-center w-[18rem] mobile:w-auto mobile:gap-xs justify-between font-medium">
                  <CheckIcon className="w-md" />
                  Yes
                  <div className="w-md" />
                </Row>
              </NormalButton>
            </Row>
          }
        >
          <div className="text-2xl text-center font-semibold my-20">Do you want to restart the game?</div>
        </ModalButton>
      )}
    </Container>
  );
};

export default GameMenuButtons;
